/**
 * Customer / partner logos for the sign-in / sign-up marquee, resolved per brand.
 *
 * Logos live in per-brand subfolders under `src/assets/onboarding/logos/<brand>/`
 * (e.g. `strivelabs/`, `aham37/`). A recursive `import.meta.glob` picks them all up
 * at build time, so surfacing a new brand's strip is just dropping in a folder — no
 * code change. Lives in the theme layer because theme definitions own brand assets;
 * consumed by `themes/*.ts` via `getMarqueeLogos(brand)`.
 */

// Recursive glob: `**` matches across path segments (plain `*` would not), so each
// brand's subfolder is included.
const logoFiles = import.meta.glob('../../assets/onboarding/logos/**/*.{svg,png,webp}', {
  eager: true,
  import: 'default',
}) as Record<string, string>;

/**
 * Marquee logos grouped by brand folder name (the segment after `/logos/`),
 * each ordered by file name for a stable, deterministic strip. Built once at load.
 */
const logosByBrand: Record<string, string[]> = {};
for (const [path, url] of Object.entries(logoFiles).sort(([a], [b]) => a.localeCompare(b))) {
  const brand = path.match(/\/logos\/([^/]+)\//)?.[1];
  if (brand) (logosByBrand[brand] ??= []).push(url);
}

/**
 * Customer / partner logos for a given brand folder (e.g. `'strivelabs'`, `'aham37'`).
 * Returns `[]` if the folder is empty/missing so the marquee simply hides rather than
 * breaking.
 */
export function getMarqueeLogos(brand: string): string[] {
  return logosByBrand[brand] ?? [];
}
